import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  Paper,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import * as Papa from 'papaparse';

interface PublisherFileUploadStepProps {
  onFileUpload: (file: File, parsedData: any) => void;
  loading: boolean;
}

const PublisherFileUploadStep: React.FC<PublisherFileUploadStepProps> = ({
  onFileUpload,
  loading,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string>('');

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  }, []);

  const handleFile = (file: File) => {
    setError('');

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    // Parse CSV file
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results: Papa.ParseResult<any>) => {
        if (results.errors.length > 0) {
          setError('Error parsing CSV: ' + results.errors[0].message);
          return;
        }

        if (results.data.length === 0) {
          setError('CSV file is empty');
          return;
        }

        // Auto-detect column mappings
        const headers = results.meta.fields || [];
        const autoMappings = headers.map((header: string) => {
          const lowerHeader = header.toLowerCase().trim();
          
          // Map common column names
          if (lowerHeader.includes('publisher') && lowerHeader.includes('name')) {
            return { csvColumn: header, dbField: 'publisherName' };
          }
          if (lowerHeader === 'email' || lowerHeader === 'publisher email') {
            return { csvColumn: header, dbField: 'email' };
          }
          if (lowerHeader === 'whatsapp' || lowerHeader === 'whatsapp number') {
            return { csvColumn: header, dbField: 'whatsapp' };
          }
          if (lowerHeader.includes('communication') || lowerHeader.includes('mode')) {
            return { csvColumn: header, dbField: 'modeOfCommunication' };
          }
          if (lowerHeader === 'status' || lowerHeader === 'active') {
            return { csvColumn: header, dbField: 'isActive' };
          }
          
          return { csvColumn: header, dbField: '' };
        });

        const parsedData = {
          headers,
          rows: results.data,
          autoMappings,
        };

        onFileUpload(file, parsedData);
      },
      error: (error: Error) => {
        setError('Failed to parse CSV: ' + error.message);
      },
    });
  };

  const downloadTemplate = () => {
    const template = `Publisher Name,Email,WhatsApp,Mode of Communication,Active
John's Tech Blog,john@techblog.com,+1234567890,EMAIL,true
Sarah's Marketing Hub,sarah@marketinghub.com,+9876543210,WHATSAPP,true
Digital Insights,contact@digitalinsights.com,,EMAIL,true`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'publisher_bulk_upload_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Required Fields:</strong> Publisher Name, Email
        </Typography>
        <Typography variant="body2">
          <strong>Optional Fields:</strong> WhatsApp, Mode of Communication (EMAIL/WHATSAPP), Active (true/false)
        </Typography>
      </Alert>

      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={downloadTemplate}
          size="small"
        >
          Download CSV Template
        </Button>
      </Box>

      <Paper
        sx={{
          border: '2px dashed',
          borderColor: dragActive ? 'primary.main' : 'grey.300',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          backgroundColor: dragActive ? 'action.hover' : 'background.paper',
          cursor: 'pointer',
          transition: 'all 0.3s',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="file-upload"
          accept=".csv"
          onChange={handleChange}
          style={{ display: 'none' }}
          disabled={loading}
        />
        <label htmlFor="file-upload" style={{ cursor: 'pointer' }}>
          <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Drag and drop your CSV file here
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            or click to browse
          </Typography>
          <Button variant="contained" component="span" disabled={loading}>
            Select File
          </Button>
        </label>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mt: 3 }}>
        <Typography variant="caption" color="text.secondary">
          <strong>CSV Format Requirements:</strong>
          <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
            <li>First row must contain column headers</li>
            <li>Publisher Name and Email are required for each row</li>
            <li>Email must be in valid format (e.g., user@example.com)</li>
            <li>Mode of Communication: EMAIL or WHATSAPP (default: EMAIL)</li>
            <li>Active: true or false (default: true)</li>
          </ul>
        </Typography>
      </Box>
    </Box>
  );
};

export default PublisherFileUploadStep;
